import type { HTMLAttributes, ReactNode } from 'react';
import type { ConnectionMode, DefaultEdgeOptions, Edge, EdgeChange, Node, NodeChange } from '../../core';
import type { CustomMindmapInstance } from '../../hooks';
import type { ConnectHandlers, DeleteHandlers, DeleteKeyCode, ElementHandlers, NodeDragHandler, NodeTypes } from '../../store';

/** A callback the engine accepts but never calls. */
type IgnoredCallback = (...args: never[]) => unknown;

/**
 * Props the app passes that the engine ignores on purpose (D-016): `proOptions` (no attribution to hide), the
 * `connectedOnClick` typo and the pre-v11 `onElementsRemove`. Accepted only so they never reach the DOM.
 */
export type PendingProps = {
  proOptions?: Record<string, unknown>;
  connectedOnClick?: boolean;
  onElementsRemove?: IgnoredCallback;
};

/** Props the engine acts on (04-api-spec.md). */
export type EngineProps = ConnectHandlers &
  DeleteHandlers &
  ElementHandlers & {
  nodes?: Node[];
  edges?: Edge[];
  /** Looks for every edge, e.g. `{ type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } }`. Render-time only. */
  defaultEdgeOptions?: DefaultEdgeOptions;
  minZoom?: number;
  maxZoom?: number;
  nodeTypes?: NodeTypes;
  /** `Loose` lets edges end on source handles, as the app does. Default `Strict` (v11). */
  connectionMode?: ConnectionMode;
  /** Receives engine changes: measured sizes, drag positions, selection, removal. */
  onNodesChange?: (changes: NodeChange[]) => void;
  /** Receives edge selection and removal changes. */
  onEdgesChange?: (changes: EdgeChange[]) => void;
  /** Default true. A node's own `draggable` wins (v11). */
  nodesDraggable?: boolean;
  onNodeDragStart?: NodeDragHandler;
  onNodeDrag?: NodeDragHandler;
  /** Also called after a plain click on a node (v11 drag threshold 0). */
  onNodeDragStop?: NodeDragHandler;
  /** Default true. Like v11 it only hides the connection line; handles still connect. */
  nodesConnectable?: boolean;
  /** Default true: click a handle, then another, to connect. */
  connectOnClick?: boolean;
  /** Snap distance to a handle while connecting, flow units. Default 20. */
  connectionRadius?: number;
  /** Called once after mount with the canvas instance (v11 `onInit`). */
  onInit?: (instance: CustomMindmapInstance) => void;
  /** Key(s) that delete the selection; `null` / `[null]` disables. Default `Backspace` (v11). */
  deleteKeyCode?: DeleteKeyCode;
};

/** Every engine prop name; a missing key is a compile error, so no engine prop can leak onto the DOM. */
const ENGINE_PROP_KEYS: Record<keyof EngineProps | keyof PendingProps, true> = {
  nodes: true,
  edges: true,
  defaultEdgeOptions: true,
  minZoom: true,
  maxZoom: true,
  nodeTypes: true,
  connectionMode: true,
  onNodesChange: true,
  nodesDraggable: true,
  onNodeDragStart: true,
  onNodeDrag: true,
  onNodeDragStop: true,
  nodesConnectable: true,
  connectOnClick: true,
  connectionRadius: true,
  onConnect: true,
  onConnectStart: true,
  onConnectEnd: true,
  onEdgesChange: true,
  deleteKeyCode: true,
  onInit: true,
  onNodeClick: true,
  onNodeDoubleClick: true,
  onNodeMouseEnter: true,
  onNodeContextMenu: true,
  onEdgeContextMenu: true,
  onPaneContextMenu: true,
  onNodesDelete: true,
  onEdgesDelete: true,
  onEdgeClick: true,
  proOptions: true,
  connectedOnClick: true,
  onElementsRemove: true,
};

export const ENGINE_PROP_NAMES: ReadonlySet<string> = new Set(Object.keys(ENGINE_PROP_KEYS));

/** Props of `<CustomMindmap>`. */
export type CustomMindmapProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> &
  PendingProps &
  EngineProps & {
    /** Overlay content (sidebar, controls, minimap); not transformed with the viewport. */
    children?: ReactNode;
  };
